'use client';
import React, { useEffect, useState } from 'react';
import { EllipseRed, SubmitYourWork, WayToCome, FreeTrial, QualificationTest } from '@/public/icon';
import { Box, ButtonBase, Typography } from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import BaseModal from '@/app/base-ui/BaseModalUI';
import PermissionWrapper from '@/app/components/Auth/PermissionWrapper';
import { useGlobalDialog } from '@/app/contexts/global-dialog';
import { useUser } from '@/app/hooks/useUser';
import { useGlobalDialogV2 } from '@/app/contexts/GlobalDialogV2';
import { challengeService } from '@/app/service/challengeService/challengeService';
import utilsService from '@/app/helpers/utilsService';
import { paymentService } from '@/app/service/payments';
import BaseCardNoticeGray from '@/app/base-ui/BaseCardNoticeGray';
import { PaymentRequestSubmitWork } from '@/app/models/payments';
import { useTranslation } from '@/app/hooks/useTranslation';
import { useMyRoomEntry } from '@/app/components/MyRoom/useMyRoomEntry';
import { useRoomEntryAllowed } from '@/app/components/MyRoom/appCompat';

const isQuickLinkEnabled = process.env.NEXT_PUBLIC_SUBMISSION_QUICK_LINK_ENABLED === 'true';
const ChallengeID = process.env.NEXT_PUBLIC_SUBMISSION_CHALLENGE_ID;
const STEPS: any = { 1: 'one', 2: 'two', 3: 'three' };

const ListActions = () => {
    const { t } = useTranslation();
    // 항목 배열을 만들기 전에 판정해야 해서 여기서 부른다.
    const roomEntryAllowed = useRoomEntryAllowed();

    const items = [
        { type: 'attendance', icon: '/icon/checkin.svg', label: t('attendance', '출석체크'), hasNotifications: false },
        { type: 'mission_participation', icon: '/icon/challenge.svg', label: t('mission_participation', '챌린지'), hasNotifications: false },
        { type: 'roulette', icon: '/icon/roulette.svg', label: t('roulette', '룰렛'), hasNotifications: false },
        // { type: 'submit-work', icon: <SubmitYourWork />, label: t('submit_work', '작품투고'), hasNotifications: process.env.NEXT_PUBLIC_SUBMISSION_QUICK_LINK_ENABLED },
        // { type: 'free_trial', icon: <FreeTrial />, label: t('free_trial', '무료체험'), hasNotifications: false },
        // { type: 'qualification_test', icon: <QualificationTest />, label: t('qualification_test', '자격시험'), hasNotifications: false },
        { type: 'post_news', icon: '/icon/news.svg', label: t('post_news', '조커소식') },
        // 마이룸 3D 전시관 진입. 시안(APP_01_Home > @퀵메뉴 > @마이룸) 기준 조커소식 뒤 5번째.
        // 마이룸을 쓸 수 없는 앱(구버전)에서는 항목을 빼서 예전처럼 4개만 보이게 한다.
        ...(roomEntryAllowed
            ? [{ type: 'myroom', icon: '/icon/myroom3d.svg', label: t('myroom_3d', '3D전시관'), hasNotifications: false }]
            : []),
        // { type: 'contact', icon: <WayToCome />, label: t('contact', '오시는길'), hasNotifications: false },
        // { icon: <QualificationTest />, label: t('qualification_test', '자격시험') },
        // { icon: <WayToCome />, label: t('contact', '오시는길') },
    ];
    const router = useRouter();
    const [openModal, setOpenModal] = useState(false);
    const { checkPermissions } = useUser();
    const [modalContent, setModalContent] = useState({ title: '', description: '' });
    const { loadingDialog } = useGlobalDialog();
    const { showDialog, hideDialog } = useGlobalDialogV2();
    const enterMyRoom = useMyRoomEntry();

    const redirectTo = async (type: string, hasNotifications: boolean) => {
        loadingDialog.open();

        // 마이룸 3D 전시관 — 방으로 바로 보낸다(연동=본인 방 / 미연동=샘플 방 / 비로그인=로그인).
        if (type === 'myroom') {
            await enterMyRoom();
            loadingDialog.close();
            return;
        }

        // Handle post_news - redirect to /community/newsletter
        if (type === 'post_news') {
            loadingDialog.close();
            return router.push('/community/newsletter');
        }

        // Handle free_trial and qualification_test
        if (['free_trial', 'qualification_test'].includes(type)) {
            const checkPermission = await checkPermissions();
            if (checkPermission) {
                loadingDialog.close();
                setTimeout(() => {
                    router.push(`/events/${type}/latest`);
                }, 0);
                return;
            } else {
                loadingDialog.close();
                setTimeout(() => {
                    // U6-6: 클릭한 이벤트로 바로 돌아오게 — 지금 경로(홈)가 아니라 목적지를 넘긴다.
                    router.push(utilsService.buildLoginUrl(`/events/${type}/latest`));
                }, 0);
                return;
            }
        }

        if (['roulette'].includes(type)) {
            loadingDialog.close();
            setTimeout(() => {
                router.push(`/events/${type}/latest`);
            }, 0);
            return;
        }
        if (!['submit-work', 'contact', 'mission_participation', 'roulette', 'post_news', 'free_trial', 'qualification_test'].includes(type)) {
            const checkPermission = await checkPermissions();
            if (checkPermission) {
                loadingDialog.close();
                setTimeout(() => {
                    router.push(`/events/${type}/latest`);
                }, 0);
                return;
            } else {
                loadingDialog.close();
                setTimeout(() => {
                    router.push(utilsService.buildLoginUrl(`/events/${type}/latest`));
                }, 0);
                return;
            }
        }
        if (['mission_participation'].includes(type)) {
            loadingDialog.close();

            // "전체" 로 연다. "참여할 수 있는" 으로 열면 진행 중인 챌린지가 없는 기간엔 빈 화면만
            // 보였다(2026-09-22 TestFlight 피드백).
            return router.push(`/community/challenges/?filtered=common`);
        }
        if (['contact'].includes(type)) {
            loadingDialog.close();
            setTimeout(() => {
                router.push(`/directions`);
            }, 0);
            return;
        }
        if (type === 'submit-work') {
            if (hasNotifications) {
                if (ChallengeID) handleLogicRedirect();
                else if (!ChallengeID) {
                    showAlert(t('must_pay_before_submit', '새 신청서를 제출하기 전에 비용을 지불해야 합니다.'));
                    loadingDialog.close();
                }
                return;
            }
            showAlert();
            loadingDialog.close();
            return;
        }
        setModalContent({
            title: t('feature_under_development', '이 기능은 열심히 개발중입니다.<br />멋진 업데이트로 찾아뵙겠습니다!'),
            description: '',
        });
        setOpenModal(true);
        loadingDialog.close();
        return;
    };

    const handleLogicRedirect = async () => {
        await handleCheckChallenge();
    };

    const fetchChallengeDetail = async (): Promise<{ isParticipated: boolean; isPaid: boolean } | undefined> => {
        try {
            if (!ChallengeID) return;
            const { data } = await challengeService.getChallengeDetails({ challengeId: ChallengeID });
            return { isParticipated: data?.is_participated, isPaid: data?.is_paid_participation };
        } catch (error) {}
    };

    const handleCheckChallenge = async () => {
        try {
            const isChecking = await fetchChallengeDetail();
            if (isChecking?.isParticipated && isChecking?.isPaid) {
                const checkIsPaid = await checkPaid();
                if (checkIsPaid && !checkIsPaid?.isPaidAlready) {
                    handleShowDialogWarning(checkIsPaid?.fee as any, checkIsPaid?.submissionId as any);
                    loadingDialog.close();
                    return;
                }
            }
            await handleLogicContinueSubmit(ChallengeID);
        } catch (e) {}
    };

    const handleLogicContinueSubmit = async (challengeId: any) => {
        try {
            const response = await challengeService.getParticipantEd({ challengeId });
            const data = response.data.submission;
            if (!data) {
                router.push(`/community/challenges/${challengeId}/submitworks/one`);
                return;
            }
            const stepToContinue = utilsService.getLastStep(data?.step);
            if (stepToContinue && stepToContinue?.toString() === '4') {
                router.push(`/community/challenges/${challengeId}/submitworks/three`);
            } else {
                if (stepToContinue && stepToContinue?.toString()) {
                    router.push(`/community/challenges/${challengeId}/submitworks/${STEPS[stepToContinue]}`);
                } else {
                    showAlert();
                    loadingDialog.close();
                }
            }
        } catch (error) {
            const message = utilsService.getErrorMessage(error);
            showAlert(message);
            loadingDialog.close();
        }
    };

    const handleAcceptForPaidFeeType = async (submissionId: number) => {
        loadingDialog.open();
        hideDialog?.();
        router.replace(`/payments/submission?id=${submissionId}&challengeId=${ChallengeID}`);
    };

    const handleShowDialogWarning = (fee: number, submissionId: number) => {
        const message = `<span style='font-weight: 500'>${t('submission_not_completed', '출품이 완료되지 않았습니다.')}</span>`;
        showDialog({
            title: message,
            message: '',
            acceptText: t('pay_submission_fee', '네, 납부할게요!'),
            cancelText: t('no', '아니오'),
            showBaseCardNotice: true,
            showAccept: true,
            showCancel: true,
            baseCardContent: renderBaseCard(`
             <span style='font-weight: 500; line-height: 100%; white-space: pre-line'>${t('challenge_fee_notice_part1', '본 챌린지는 참가비')}  <span style='font-size: 12px;color: #FF2164;'>${utilsService.formatNumber(
                 fee ?? 0
             )}${t('challenge_fee_notice_part2', '원을 납부해야')}<br/><span>${t('challenge_fee_notice_part3', '출품이 완료됩니다.\n\n참가하시겠습니까?')}</span>
             </span>`),
            onAccept: () => handleAcceptForPaidFeeType(submissionId),
            onCancel: hideDialog,
        });
    };

    const renderBaseCard = (content: string) => {
        return (
            <BaseCardNoticeGray
                content={content}
                style={{ height: '63px', flexShrink: 0 }}
                sx={{ lineHeight: '100%' }}
            />
        );
    };

    const checkPaid = async (): Promise<{ isPaidAlready: boolean; fee: number; submissionId: number } | undefined> => {
        try {
            if (!ChallengeID) return;
            const { data } = await paymentService.checkPaid(ChallengeID as any);
            if (data !== null) {
                return { isPaidAlready: data?.isPaid, fee: data?.price, submissionId: data?.submissionId };
            }
            return undefined;
        } catch (error) {
            // return undefined;
        }
    };
    // /payments/submission?id=192&challengeId=121
    const showAlert = (message?: string) => {
        showDialog({
            message: '',
            title: message ?? t('no_active_submission_challenge', '진행중인 작품투고 챌린지가 없습니다.'),
            acceptText: t('confirm', '확인'),
            onAccept: hideDialog,
            showAccept: true,
            showCancel: false,
        });
    };

    return (
        <>
            {/* Desktop/Tablet: Keep original flex layout */}
            <Box
                sx={{
                    paddingTop: '18px',
                    paddingBottom: '18px',
                    paddingRight: '20px',
                    paddingLeft: '20px',
                    gap: 15,
                    display: { tablet: 'flex', laptop: 'flex', mobile: 'none' }
                }}
            >
                {items.map((item, index) => {
                    const ItemContent = (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                alignItems: 'center',
                                gap: 2.5,
                                cursor: 'pointer',
                            }}
                            component="div"
                            onClick={() => redirectTo(item.type, isQuickLinkEnabled)}
                        >
                            <ButtonBase
                                component="div"
                                sx={{ borderRadius: 1, padding: 2, margin: -2 }}
                            >
                                {typeof item.icon === 'string' ? (
                                    <Image
                                        src={item.icon}
                                        alt={item.label}
                                        width={48}
                                        height={48}
                                        draggable={false}
                                        onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                        style={{ objectFit: 'contain' }}
                                    />
                                ) : (
                                    item.icon
                                )}
                            </ButtonBase>

                            {item.hasNotifications ? (
                                <Box sx={{ position: 'absolute', bottom: 5, right: 0 }}>
                                    <EllipseRed />
                                </Box>
                            ) : null}

                            <Typography
                                variant="h2_10Medium"
                                lineHeight="100%"
                            >
                                {item.label}
                            </Typography>
                        </Box>
                    );

                    if (['attendance', 'roulette', 'submit-work'].includes(item.type)) {
                        return (
                            <PermissionWrapper key={index}>
                                {ItemContent}
                            </PermissionWrapper>
                        );
                    }

                    return <React.Fragment key={index}>{ItemContent}</React.Fragment>;
                })}
            </Box>
            {/* Mobile: 4 equal columns using grid */}
            <Box
                sx={{
                    paddingTop: '18px',
                    paddingBottom: '18px',
                    paddingRight: '20px',
                    paddingLeft: '20px',
                    display: { tablet: 'none', laptop: 'none', mobile: 'grid' },
                    // 항목 수에 맞춰 한 줄로 편다(시안: 5개 한 줄). 하드코딩 4 면 5번째가 줄바꿈된다.
                    gridTemplateColumns: `repeat(${items.length}, 1fr)`,
                    gap: 2,
                    maxWidth: '370px',
                    margin: 'auto'
                }}
            >
                {items.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            alignItems: 'center',
                            gap: 2.5,
                            cursor: 'pointer',
                        }}
                        onClick={() => redirectTo(item.type, isQuickLinkEnabled)}
                    >
                        <ButtonBase
                            component="div"
                            sx={{ borderRadius: 1, padding: 0, margin: -2 }}
                        >
                            {typeof item.icon === 'string' ? (
                                <Image
                                    src={item.icon}
                                    alt={item.label}
                                    width={48}
                                    height={48}
                                    draggable={false}
                                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                    style={{ objectFit: 'contain' }}
                                />
                            ) : (
                                item.icon
                            )}
                        </ButtonBase>
                        {item.hasNotifications ? (
                            <Box sx={{ position: 'absolute', bottom: 5, right: 0 }}>
                                <EllipseRed />
                            </Box>
                        ) : null}
                        <Typography
                            variant="h2_10Medium"
                            lineHeight="100%"
                            sx={{ textAlign: 'center', fontSize: '10px' }}
                        >
                            {item.label}
                        </Typography>
                    </Box>
                ))}
            </Box>
            {openModal && (
                <BaseModal
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    title={modalContent.title}
                    description={modalContent.description}
                    showAccept={true}
                    showCancel={false}
                    onCancel={() => setOpenModal(false)}
                    onAccept={() => setOpenModal(false)}
                    acceptText={t('confirm', '확인')}
                />
            )}
        </>
    );
};

export default ListActions;
